import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, Target, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const AboutPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'nl' ? 'Terug' : 'Back'}
          </button>

          {/* Hero */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'nl' ? 'Over Humanistix' : 'About Humanistix'}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Wij helpen HR-teams om AI te omarmen en hun organisatie naar een hoger niveau te tillen.'
                : 'We help HR teams embrace AI and take their organization to the next level.'}
            </p>
          </div>

          {/* Mission */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="h-6 w-6 text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {language === 'nl' ? 'Onze Missie' : 'Our Mission'}
              </h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {language === 'nl' 
                ? 'Humanistix is opgericht met één doel: de kloof tussen HR en AI overbruggen. We geloven dat technologie mensen moet versterken, niet vervangen. Door praktische tools, quickscans en workshops bieden we HR-professionals de kennis en middelen om AI succesvol te implementeren.'
                : 'Humanistix was founded with one goal: bridging the gap between HR and AI. We believe technology should empower people, not replace them. Through practical tools, quickscans, and workshops, we provide HR professionals with the knowledge and resources to successfully implement AI.'}
            </p>
          </div>

          {/* Values */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'nl' ? 'Mens Centraal' : 'Human-Centered'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'nl' 
                  ? 'AI moet mensen ondersteunen en versterken. Wij focussen altijd op de menselijke factor bij elke implementatie.'
                  : 'AI should support and empower people. We always focus on the human factor in every implementation.'}
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                <Award className="h-5 w-5 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'nl' ? 'Praktisch & Resultaatgericht' : 'Practical & Results-Driven'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'nl' 
                  ? 'Geen theoretisch gepraat, maar concrete use cases en werkende oplossingen die direct waarde toevoegen.'
                  : 'No theoretical talk, but concrete use cases and working solutions that add immediate value.'}
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                <Building2 className="h-5 w-5 text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'nl' ? 'Belgische Expertise' : 'Belgian Expertise'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'nl' 
                  ? 'Gevestigd in Gent, met diepe kennis van de Belgische en Europese HR-markt en regelgeving.'
                  : 'Based in Ghent, with deep knowledge of the Belgian and European HR market and regulations.'}
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <Target className="h-5 w-5 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {language === 'nl' ? 'Gratis Tools' : 'Free Tools'}
              </h3>
              <p className="text-gray-600 text-sm">
                {language === 'nl' 
                  ? 'Onze AI for HR Quickscan en Use Cases Hub zijn 100% gratis toegankelijk voor iedereen.'
                  : 'Our AI for HR Quickscan and Use Cases Hub are 100% free and accessible to everyone.'}
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              {language === 'nl' ? 'Klaar om te starten?' : 'Ready to get started?'}
            </h2>
            <p className="text-blue-100 mb-6">
              {language === 'nl' 
                ? 'Ontdek uw AI-gereedheid met onze gratis quickscan.'
                : 'Discover your AI readiness with our free quickscan.'}
            </p>
            <Button 
              onClick={() => navigate('/quickscan/start')}
              className="bg-white text-blue-600 hover:bg-blue-50"
            >
              {language === 'nl' ? 'Start Gratis Quickscan' : 'Start Free Quickscan'}
            </Button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
